import { createStore } from '@globals/createStore'
import { useCallback, useEffect } from 'react'
import { useShallow } from 'zustand/shallow'

type DialogDetails<T = unknown> = {
  isOpen: boolean
  dialogData?: T
}

type DialogStoreState = {
  dialogs: Record<string, DialogDetails>
  open: <T>(key: string, data?: T) => void
  close: (key: string) => void
  setDialogData: <T>(key: string, data: T) => void
}

const useDialogStore = createStore<DialogStoreState>(set => ({
  dialogs: {},
  open: (key, data) =>
    set(
      state => ({
        dialogs: { ...state.dialogs, [key]: { isOpen: true, dialogData: data } },
      }),
      false,
      `${key}_Open`,
    ),
  close: key =>
    set(
      state => ({
        dialogs: {
          ...state.dialogs,
          [key]: { ...state.dialogs[key], isOpen: false, dialogData: null },
        },
      }),
      false,
      `${key}_Close`,
    ),
  setDialogData: (key, data) =>
    set(state => ({
      dialogs: {
        ...state.dialogs,
        [key]: {
          ...state.dialogs[key],
          dialogData: data,
        },
      },
    })),
}))

export default useDialogStore

/**
 * Subscribes to the global dialog store for a single dialog `key` and returns
 * its open state, optional typed payload, and imperative helpers.
 *
 * `openDialog`, `closeDialog`, and `setData` are wrapped in `useCallback` with
 * stable store actions (`open`, `close`, `setDialogData`) plus `key`, so their
 * identities stay stable across renders unless `key` changes. Safe to list in
 * React dependency arrays (e.g. `useEffect` cleanups) without spurious reruns
 * or update-depth loops from recreated inline closures.
 *
 * @param key - Unique dialog id; must match the key used by dialog UI / callers.
 * @returns Dialog visibility, payload, and bound open/close/set helpers for this key.
 */
export const useDialog = <T = unknown>(key: string) => {
  const { dialogs, open, close, setDialogData } = useDialogStore(
    useShallow(state => ({
      dialogs: state.dialogs,
      open: state.open,
      close: state.close,
      setDialogData: state.setDialogData,
    })),
  )

  const dialog = (dialogs[key] as DialogDetails<T>) ?? {
    isOpen: false,
    dialogData: undefined,
  }

  const openDialog = useCallback(
    (data?: T) => {
      open<T>(key, data)
    },
    [open, key],
  )

  const closeDialog = useCallback(() => {
    close(key)
  }, [close, key])

  const setData = useCallback(
    (data: T) => {
      setDialogData(key, data)
    },
    [setDialogData, key],
  )

  return {
    isOpen: dialog.isOpen,
    dialogData: dialog.dialogData,
    openDialog,
    closeDialog,
    setData,
  }
}

/**
 * Marks the dialog `key` closed when the calling component unmounts, and
 * whenever `subject` changes — the thing the open dialog is about, such as the
 * member whose teams it lists.
 *
 * Callers that render a dialog only while the store says it is open need this:
 * an unmount that isn't a close (leaving the profile About tab, navigating away
 * from the People page) would otherwise leave the flag set, and the dialog would
 * reopen on its own the next time any caller sharing the key mounts. Passing a
 * `subject` covers the case where nothing unmounts at all: a caller that stays
 * mounted while its subject changes (moving between member profiles on the same
 * route) would keep a dialog open whose payload still describes the old subject.
 *
 * Call this from the component that decides whether to mount the dialog, never
 * from the dialog itself — a dialog gated on `isOpen` would close itself as soon
 * as React Strict Mode replays the cleanup right after mounting.
 */
export const useAutoCloseDialog = (key: string, subject?: unknown) => {
  useEffect(
    () => () => {
      const { dialogs, close } = useDialogStore.getState()
      if (dialogs[key]?.isOpen) {
        close(key)
      }
    },
    [key, subject],
  )
}
